/**
 * WebSocket connection for real-time events (API §3).
 * Connects when authenticated, subscribes to current studio (or all studios when "all").
 * Dispatches custom events so Chats/Dashboard can refetch without polling.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { getAccessToken } from "@/lib/api-client";
import { runProactiveRefreshIfNeeded } from "@/lib/auth-refresh-coordinator";
import { encodeWsBearerSubprotocol } from "@/lib/ws-auth";
import {
  TOKEN_REFRESHED_EVENT,
  WS_AGENT_WORKFLOW_EVENT,
  WS_CONNECTION_HEALTH_EVENT,
  WS_CONVERSATION_EVENT,
  WS_DASHBOARD_EVENT,
  WS_SESSION_EVENT,
  WS_SYNC_EVENT,
} from "@/lib/ws-events";
import { getWebSocketUrl } from "@/lib/ws-url";
import { useAuth } from "./AuthContext";
import { useStudio } from "./StudioContext";

export {
  WS_AGENT_WORKFLOW_EVENT,
  WS_CONNECTION_HEALTH_EVENT,
  WS_CONVERSATION_EVENT,
  WS_DASHBOARD_EVENT,
  WS_SESSION_EVENT,
  WS_SYNC_EVENT,
} from "@/lib/ws-events";

const CONVERSATION_EVENT_TYPES = new Set([
  "message.new",
  "message.updated",
  "message.delivery_failed",
  "draft.ready",
  "draft.failed",
  "conversation.new",
  "conversation.updated",
  "conversation.status_changed",
  "conversation.archived",
  "conversation.restored",
  "conversation.purged",
  "conversation.identity_approved",
]);

const SESSION_EVENT_TYPES = new Set(["session.scope_changed"]);

const SYNC_EVENT_TYPES = new Set([
  "sync_progress",
  "sync_completed",
  "sync_failed",
]);
const AGENT_WORKFLOW_EVENT_TYPES = new Set(["agent_workflow_progress"]);

/** Client liveness: ping every 20s; treat socket dead if no pong within 10s. */
export const WS_PING_MS = 20_000;
export const WS_PONG_TIMEOUT_MS = 10_000;
/** After a non-auth close: first retry at 1s, then ×2, cap 30s. Reset on open. */
export const WS_RECONNECT_INITIAL_MS = 1_000;
export const WS_RECONNECT_MAX_MS = 30_000;

interface WebSocketContextType {
  connected: boolean;
}

const WebSocketContext = createContext<WebSocketContextType>({ connected: false });

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { activeStudioId } = useStudio();
  const [connected, setConnected] = useState(false);
  const [wsSessionKey, setWsSessionKey] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const subscribedStudiosRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onTokenRefreshed = () => {
      setWsSessionKey((key) => key + 1);
    };
    window.addEventListener(TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, onTokenRefreshed);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        subscribedStudiosRef.current = new Set();
        setConnected(false);
      }
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;
    let cancelled = false;
    let pingInterval: ReturnType<typeof setInterval> | null = null;
    let pongTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearHeartbeat = () => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (pongTimeout) {
        clearTimeout(pongTimeout);
        pongTimeout = null;
      }
    };

    const clearReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const startHeartbeat = () => {
      clearHeartbeat();
      pingInterval = setInterval(() => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({ type: "ping" }));
        if (pongTimeout) clearTimeout(pongTimeout);
        pongTimeout = setTimeout(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close();
          }
        }, WS_PONG_TIMEOUT_MS);
      }, WS_PING_MS);
    };

    const scheduleReconnect = () => {
      if (cancelled || !isAuthenticated) return;
      const delay = Math.min(
        WS_RECONNECT_INITIAL_MS * 2 ** reconnectAttempt,
        WS_RECONNECT_MAX_MS,
      );
      reconnectAttempt += 1;
      reconnectTimer = setTimeout(() => {
        if (cancelled) return;
        openSocket();
      }, delay);
    };

    const openSocket = () => {
      if (cancelled) return;
      const token = getAccessToken();
      if (!token) return;

      const url = getWebSocketUrl();
      const protocols = [encodeWsBearerSubprotocol(token)];
      let socket: WebSocket;
      try {
        socket = new WebSocket(url, protocols);
      } catch {
        setConnected(false);
        scheduleReconnect();
        return;
      }

      ws = socket;
      wsRef.current = socket;

      socket.onopen = () => {
        if (socket !== ws) return;
        reconnectAttempt = 0;
        setConnected(true);
        startHeartbeat();
      };

      socket.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data as string);
          const type = data?.type;
          if (!type) return;

          if (type === "pong") {
            if (pongTimeout) {
              clearTimeout(pongTimeout);
              pongTimeout = null;
            }
            return;
          }

          if (type === "connection_confirmed") {
            return;
          }

          if (type === "connection_health_changed") {
            window.dispatchEvent(
              new CustomEvent(WS_CONNECTION_HEALTH_EVENT, { detail: data }),
            );
            return;
          }

          if (CONVERSATION_EVENT_TYPES.has(type)) {
            window.dispatchEvent(
              new CustomEvent(WS_CONVERSATION_EVENT, { detail: { type, ...data } }),
            );
            return;
          }
          if (SESSION_EVENT_TYPES.has(type)) {
            window.dispatchEvent(
              new CustomEvent(WS_SESSION_EVENT, { detail: { type, ...data } }),
            );
            return;
          }
          if (type === "campaign.configuration_invalidated") {
            window.dispatchEvent(new CustomEvent("oktogo:campaign-configuration-invalidated", { detail: data }));
            return;
          }
          if (type === "dashboard.updated") {
            window.dispatchEvent(
              new CustomEvent(WS_DASHBOARD_EVENT, { detail: { type, ...data } }),
            );
          }
          if (SYNC_EVENT_TYPES.has(type)) {
            window.dispatchEvent(
              new CustomEvent(WS_SYNC_EVENT, { detail: { type, ...data } }),
            );
            return;
          }
          if (AGENT_WORKFLOW_EVENT_TYPES.has(type)) {
            window.dispatchEvent(
              new CustomEvent(WS_AGENT_WORKFLOW_EVENT, { detail: { type, ...data } }),
            );
          }
        } catch {
          // ignore non-JSON or malformed
        }
      };

      socket.onclose = (event) => {
        if (socket !== ws) return;
        clearHeartbeat();
        setConnected(false);
        wsRef.current = null;
        ws = null;
        subscribedStudiosRef.current = new Set();
        if (cancelled || !isAuthenticated) return;
        if (event.code === 4001) {
          void runProactiveRefreshIfNeeded();
          return;
        }
        scheduleReconnect();
      };

      socket.onerror = () => {
        // Close will follow
      };
    };

    openSocket();

    return () => {
      cancelled = true;
      clearReconnect();
      clearHeartbeat();
      const current = ws ?? wsRef.current;
      if (current) {
        current.close();
      }
      ws = null;
      wsRef.current = null;
      subscribedStudiosRef.current = new Set();
      setConnected(false);
    };
  }, [isAuthenticated, user?.id, wsSessionKey]);

  // Subscribe to studio/studios when connection is open
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !user?.studios?.length) return;

    const toSubscribe =
      activeStudioId === "all"
        ? user.studios.map((s) => s.id)
        : [activeStudioId];

    for (const studioId of toSubscribe) {
      if (!studioId || subscribedStudiosRef.current.has(studioId)) continue;
      subscribedStudiosRef.current.add(studioId);
      ws.send(JSON.stringify({ type: "subscribe_studio", studioId }));
    }

    const currentSet = new Set(toSubscribe);
    for (const id of Array.from(subscribedStudiosRef.current)) {
      if (!currentSet.has(id)) {
        subscribedStudiosRef.current.delete(id);
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "unsubscribe_studio", studioId: id }));
        }
      }
    }
  }, [connected, activeStudioId, user?.studios]);

  return (
    <WebSocketContext.Provider value={{ connected }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
